import { MenuFoldOutlined, MenuOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import { Button, theme } from 'antd';
import type { ReactNode } from 'react';
import { PressRipple } from '@/components/PressRipple';
import { appInfo } from '@/config/app';
import { useShellText } from '@/hooks/useShellText';
import { useAppLayoutStyles } from './styles';

export function ShellHeader({
  brandWidth,
  compactBrand = false,
  collapsed,
  isPhone,
  showSidebar,
  onCollapse,
  onHome,
  leading,
  actions,
  secondaryTitle,
}: {
  brandWidth: number;
  compactBrand?: boolean;
  collapsed: boolean;
  isPhone: boolean;
  showSidebar: boolean;
  onCollapse: () => void;
  onHome: () => void;
  leading: ReactNode;
  actions: ReactNode;
  secondaryTitle?: string;
}) {
  const { token } = theme.useToken();
  const { styles } = useAppLayoutStyles();
  const t = useShellText();
  const secondaryWidth = secondaryTitle !== undefined ? 232 : 0;
  return (
    // 仅代理标记的头部按钮，顶部导航继续使用自身的菜单波纹。
    <PressRipple targetSelector="button[data-press-ripple-target]">
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          minWidth: 0,
          height: 56,
          // Header 内的 Menu 继承此行高，内容需扣除底边框。
          lineHeight: `${56 - token.lineWidth}px`,
          background: token.colorBgContainer,
          borderBottom: `${token.lineWidth}px ${token.lineType} ${token.colorBorderSecondary}`,
        }}
      >
        {!isPhone && (
          <div
            data-sidebar-width
            className={styles.headerSidebar}
            style={{ width: brandWidth + secondaryWidth }}
          >
            <Button
              type="text"
              onClick={onHome}
              aria-label={t('返回仪表盘', 'Go to dashboard')}
              style={{
                background: 'transparent',
                color: token.colorText,
                boxShadow: 'none',
                WebkitTapHighlightColor: 'transparent',
                // 双列品牌区固定宽度，二级标题装卸不能推动 Logo。
                flex: compactBrand ? `0 0 ${brandWidth}px` : '1 1 0',
                minWidth: 0,
                // 几何尺寸只跟随父容器，不再叠加 Button 自带的缓动曲线。
                transitionProperty: 'color, background-color, border-color, box-shadow',
                // 填满扣除底边框后的内容高度，避免悬停背景越过分隔线。
                height: '100%',
                borderRadius: 0,
                border: 0,
                padding: 16,
                gap: 8,
                justifyContent:
                  brandWidth === 80 && showSidebar ? 'center' : 'flex-start',
                overflow: 'hidden',
              }}
            >
              <span aria-hidden className={styles.brandMark} />
              {(brandWidth > 80 || !showSidebar) && (
                <strong
                  style={{
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 18,
                    fontWeight: 600,
                    lineHeight: '32px',
                  }}
                >
                  {appInfo.name}
                </strong>
              )}
            </Button>
            {secondaryTitle !== undefined && (
              <strong
                style={{
                  flex: `0 0 ${secondaryWidth}px`,
                  fontSize: 18,
                  fontWeight: 600,
                  lineHeight: '32px',
                  width: secondaryWidth,
                  paddingInline: 24,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {secondaryTitle}
              </strong>
            )}
            {showSidebar && (
              <span
                aria-hidden
                data-sidebar-divider
                style={{
                  position: 'absolute',
                  insetBlock: 0,
                  // 分隔线属于宽度容器，在过渡的每一帧都与其末端对齐。
                  insetInlineEnd: 0,
                  width: 1,
                  backgroundColor: token.colorBorderSecondary,
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              />
            )}
          </div>
        )}
        {showSidebar && (
          <Button
            data-press-ripple-target
            type="text"
            aria-label={
              collapsed
                ? t('展开菜单', 'Expand navigation')
                : t('收起菜单', 'Collapse navigation')
            }
            icon={
              isPhone ? (
                <MenuOutlined />
              ) : collapsed ? (
                <MenuUnfoldOutlined />
              ) : (
                <MenuFoldOutlined />
              )
            }
            onClick={onCollapse}
            style={{
              flex: '0 0 56px',
              width: 56,
              height: '100%',
              borderRadius: 0,
              border: 0,
            }}
          />
        )}
        <div
          style={{
            flex: '1 1 auto',
            minWidth: 0,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            // Layout.Header makes Menu inherit its line-height. Size this
            // content wrapper inside the header's separator, as TopNavHeader does.
            lineHeight: `${56 - token.lineWidth}px`,
            overflow: 'hidden',
          }}
        >
          {leading}
        </div>
        <div
          style={{
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            marginInlineEnd: isPhone ? 8 : 16,
          }}
        >
          {actions}
        </div>
      </div>
    </PressRipple>
  );
}
